/**
 * AI Forecast for a Baseline Accounting project - IAS accounting's pipeline, on the workbook.
 *
 * The forecasters read the workbook's own filed years, and the forecast is written into
 * its forecast columns - the same cells the import's starting forecast fills. Running and
 * APPLYING stay separate, as in IAS accounting: applying overwrites figures someone may
 * have typed, so it is a decision made after seeing them, and it is saved as a version.
 */

import { useState } from 'react';

import { WorkingIndicator } from '../../../components/WorkingIndicator';
import { useMAccountingStore } from '../../../store/m-accounting';
import { periodLabel } from '../../../store/m-accounting';
import { CollapsibleNotes } from '../CollapsibleNotes';
import { Markdown } from '../Markdown';
import { PosteriorPanel } from '../accounting/ForecastViewV2';
import { Button } from '../ui/Button';

export function MForecast() {
  const computed = useMAccountingStore((s) => s.computed);
  const forecast = useMAccountingStore((s) => s.forecast);
  const isForecasting = useMAccountingStore((s) => s.isForecasting);
  const progress = useMAccountingStore((s) => s.forecastProgress);
  const error = useMAccountingStore((s) => s.forecastError);
  const runForecast = useMAccountingStore((s) => s.runForecast);
  const applyForecast = useMAccountingStore((s) => s.applyForecast);

  const [confirming, setConfirming] = useState(false);
  const [appliedNotes, setAppliedNotes] = useState<string[] | null>(null);
  const [showMethod, setShowMethod] = useState(false);

  if (!computed) return null;
  const years = computed.forecast;
  const span = years.length
    ? `${periodLabel(computed, years[0])}–${periodLabel(computed, years[years.length - 1])}`
    : '—';
  // The workbook's own currency, from its Settings tab (C3).
  const settings = computed.tabs.find((t) => t.sheet === 'Settings');
  const currencyCell = settings?.rows.find((r) => r.row === 3)?.cells.C?.value;
  const currency = typeof currencyCell === 'string' && currencyCell ? currencyCell : 'GBP';
  const unbalanced = Object.entries(computed.checks.balanceCheck).filter(
    ([, v]) => typeof v !== 'number' || Math.abs(v) > 1,
  );

  return (
    <div className="max-w-4xl space-y-4">
      <div>
        <h2 className="font-display text-lg font-bold text-gray-900">AI forecast</h2>
        <p className="mt-1 text-sm text-gray-600">
          Forecasts revenue, staffing, overheads and capex for the baseline model's forecast
          years ({span}) from its own filed years, using sector benchmarks and peer data - the
          same pipeline IAS accounting runs. Applying it writes the figures into the forecast
          columns; the workbook's own rules then work out depreciation, tax, working capital
          and cash.
        </p>
      </div>

      {unbalanced.length > 0 && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
          The baseline model does not balance in {unbalanced.map(([key]) => periodLabel(computed, key)).join(', ')}.
          Every driver is measured from this history, so correct that first for a forecast
          worth reading.
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <Button variant="primary" disabled={isForecasting || years.length === 0} onClick={() => void runForecast(false)}>
          {isForecasting ? 'Forecasting…' : forecast ? 'Run again' : 'Run forecast'}
        </Button>
        {forecast && (
          <Button variant="quiet" disabled={isForecasting} onClick={() => void runForecast(true)}>
            Regenerate (ignore cache)
          </Button>
        )}
      </div>

      {isForecasting && (
        <div className="rounded-xl border border-accent-200 bg-accent-50 px-4 py-3 text-sm text-gray-900">
          <WorkingIndicator
            label="Running four forecasters and a Monte Carlo simulation"
            detail={progress || undefined}
          />
          <p className="mt-1.5 text-xs text-gray-600">
            A minute or two the first time; repeat runs are cached. It carries on if you move
            to another tab.
          </p>
        </div>
      )}

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">{error}</div>
      )}

      {forecast && !isForecasting && (
        <>
          <div className="rounded-xl border border-gray-200 bg-white p-5">
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Forecast years</div>
            <div className="mt-1 font-display text-2xl font-bold text-gray-900">{span}</div>
            <div className="mt-1 text-xs text-gray-500">
              From {forecast.nSamples.toLocaleString()} simulations{forecast.cacheHit && ' · served from cache'}
            </div>
          </div>

          <CollapsibleNotes title="Points to check" notes={forecast.forecasterWarnings} />

          <div className="flex items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white px-4 py-3">
            <div>
              <div className="text-sm font-semibold text-gray-900">Apply to the baseline model</div>
              <div className="mt-0.5 text-xs text-gray-600">
                Writes the forecast into the forecast columns of the P&amp;L, Staffing, Indirect
                Costs and Fixed Assets tabs, as a new version - undo it from Versions.
              </div>
            </div>
            <Button variant="secondary" onClick={() => setConfirming(true)}>Apply</Button>
          </div>

          {appliedNotes && (
            <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
              Applied as a new version.
              {appliedNotes.length > 0 && (
                <ul className="mt-1 list-disc pl-5 text-xs">
                  {appliedNotes.map((note) => <li key={note}>{note}</li>)}
                </ul>
              )}
            </div>
          )}

          <PosteriorPanel
            summaries={forecast.posteriorSummaries as unknown as Record<string, Record<string, number>>}
            currency={currency}
          />

          {forecast.methodology && (
            <div className="rounded-xl border border-gray-200 bg-white">
              <button
                type="button"
                onClick={() => setShowMethod((open) => !open)}
                className="flex w-full items-center justify-between px-4 py-2.5 text-left"
              >
                <span className="text-sm font-semibold text-gray-700">How this was produced</span>
                <span className="text-xs text-gray-500">{showMethod ? 'Hide' : 'Show'}</span>
              </button>
              {showMethod && (
                <div className="border-t border-gray-200 px-4 py-3">
                  <Markdown>{forecast.methodology}</Markdown>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {confirming && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-lg">
            <h3 className="text-sm font-semibold text-gray-900">Apply the forecast?</h3>
            <p className="mt-2 text-sm text-gray-600">
              This overwrites the forecast years' revenue, staffing, overheads and additions.
              The project as it stands is kept as the previous version.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="quiet" onClick={() => setConfirming(false)}>Cancel</Button>
              <Button
                variant="primary"
                onClick={async () => {
                  setConfirming(false);
                  setAppliedNotes(await applyForecast());
                }}
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
